import { FC, useState, useCallback, useMemo } from 'react'
import cn from 'classnames'
import * as oldStyles from 'styles/molecules/LinkedAccounts.css'
import styles from 'components/features/pfm/LinkedAccounts/LinkedAccounts.styl'
import itemStyles from 'components/features/pfm/LinkedAccounts/LinkedAccount.styl'
import { CardBlock } from 'components/features/pfm/Blocks/Blocks'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { Guards } from 'interfaces/guards'
import { connectShadedLoadable } from 'helpers/loadableHelpers'
import { NoAccountsCard } from 'components/features/pfm/NoAccountsCard/NoAccountsCard'
import { linkedMixedAccountsGuard } from 'guards/linkageGuards'
import { mixedAccountsTableSelector, MixedAccountsTableItem } from 'selectors/linkageSelectors'
import { OrderBy, SortProps, SortParam, SortDirection } from 'components/features/pfm/LinkedAccounts/OrderBy'
import { LinkedAccount } from 'components/features/pfm/LinkedAccounts/LinkedAccount'
import Button from 'components/features/pfm/Button/Button'
import Anchor from 'components/atoms/navigation/Anchor'
import { getProviderAccountAnchorId, loadOrderState, saveOrderState } from 'helpers/linkageHelpers'
import FlipMove from 'react-flip-move'
import { clearRecentlyAddedActionCreator } from 'actions/linkageActions'
import { groupAccounts, invertDirection } from 'helpers/linkedAccountsHelper'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'

const DEFAULT_COLS_DIRECTIONS: Record<SortParam, SortDirection> = {
  providerName: 'asc',
  name: 'asc',
  amount: 'desc',
  categoryName: 'asc',
}

type LinkedAccountsProps = {}

const LinkedAccountsComponent: FC<LinkedAccountsProps> = (props) => {
  const {} = props

  const initialOrder: SortProps = useMemo(() => {
    return loadOrderState() || {
      sortBy: 'providerName',
      direction: DEFAULT_COLS_DIRECTIONS['providerName'],
    }
  }, [])

  const [sortState, setSortState] = useState(initialOrder)
  const accountsTableItems = useStoreSelector(mixedAccountsTableSelector)

  const groupedAccountsTableItems = useMemo(() => {
    if (!accountsTableItems) {
      return null
    }

    return groupAccounts(accountsTableItems, sortState.sortBy, sortState.direction)
  }, [accountsTableItems, sortState])

  const dispatch = useStoreDispatch()

  const handleFooterClick = useCallback(() => {
    dispatch(modifyFloatingPaneActionCreator('provider-search', {}))
  }, [dispatch])

  const handleHeaderClick = useCallback<(sortKey: SortParam) => void>((nextSortBy) => {
    dispatch(clearRecentlyAddedActionCreator.request({}))

    const { sortBy: prevSortBy } = sortState
    let nextSortState: SortProps
    if (nextSortBy === prevSortBy) {
      nextSortState = {
        sortBy: prevSortBy,
        direction: invertDirection(sortState.direction),
      }
    } else {
      nextSortState = {
        sortBy: nextSortBy,
        direction: DEFAULT_COLS_DIRECTIONS[nextSortBy],
      }
    }
    saveOrderState(nextSortState)
    setSortState(nextSortState)
  }, [sortState, setSortState, dispatch])

  const renderHeaderCell = useCallback(({ title, sortKey }: { title: string, sortKey: SortParam }) => (
    <div className={ cn(itemStyles[sortKey], itemStyles.headerCell) }>
      <OrderBy
        direction={ sortKey === sortState.sortBy ? sortState.direction : null }
        onClick={ () => handleHeaderClick(sortKey) }
      >
        { title }
      </OrderBy>
    </div>
  ), [handleHeaderClick, sortState])

  const accountsChildren = useMemo(() => {
    if (!groupedAccountsTableItems) {
      return null
    }
    const { length } = groupedAccountsTableItems

    let prevGroupKey: MixedAccountsTableItem[keyof MixedAccountsTableItem] | null = null
    const children = groupedAccountsTableItems.map((accountInfo, index) => {
      const { inSpan, account, providerAccount, provider, key, type, groupSum, groupByValue } = accountInfo
      const {
        link_status = null,
        cid = null,
      } = account || {}
      const { cid: providerAccountCid = null } = providerAccount || {}
      const { cid: providerCid = null } = provider || {}

      const className = cn(itemStyles.accountItem, {
        [itemStyles.unlinked]: link_status === 'unlinked',
      })

      const isFirst = prevGroupKey === null || prevGroupKey !== groupByValue
      prevGroupKey = groupByValue

      const child = (
        <div
          key={ key || index }
          className={ className }
          data-p-cid={ providerCid }
          data-pa-cid={ providerAccountCid }
          data-pba-cid={ cid }
          data-group-key={ groupByValue }
          data-is-first={ isFirst }
          data-in-span={ inSpan }
        >
          <LinkedAccount
            accountInfo={ accountInfo }
            isFirst={ isFirst }
            inSpan={ inSpan }
            menuDirection={ length - index > 2 ? 'below' : 'above' }
            groupSum={ groupSum }
            sortBy={ sortState.sortBy }
          />
        </div>
      )

      const isManual = type === 'manual'
      const anchorCid = isManual ? cid : providerAccountCid
      const hasAnchor = isManual || isFirst

      if (hasAnchor && anchorCid) {
        return (
          <Anchor key={ key || index } placement={ getProviderAccountAnchorId(anchorCid) }>
            { child }
          </Anchor>
        )
      }

      return child
    })

    return (
      <FlipMove>
        { children }
        <Button text='Add More' iconSlug='plus' theme='lightBlue' fullWidth onClick={ handleFooterClick } />
      </FlipMove>
    )
  }, [groupedAccountsTableItems, handleFooterClick, sortState.sortBy])

  if (!groupedAccountsTableItems || !groupedAccountsTableItems.length) {
    return (
      <NoAccountsCard>
        See all your accounts in one place.
      </NoAccountsCard>
    )
  }

  return (
    <CardBlock className={ styles.linkedAccounts } shadowTheme='medium' forceRoundedCorners>
      <div key='header' className={ oldStyles.header }>
        { renderHeaderCell({ title: 'INSTITUTION', sortKey: 'providerName' }) }
        { renderHeaderCell({ title: 'ACCOUNT', sortKey: 'name' }) }
        { renderHeaderCell({ title: 'BALANCE', sortKey: 'amount' }) }
        { renderHeaderCell({ title: 'TYPE', sortKey: 'categoryName' }) }
      </div>
      { accountsChildren }
    </CardBlock>
  )
}

const guards: Guards<LinkedAccountsProps> = [
  linkedMixedAccountsGuard,
]

export const LinkedAccounts = connectShadedLoadable<
  {}, {}, LinkedAccountsProps
>(() => () => ({}), () => ({}), guards)(LinkedAccountsComponent)
